import { orgSelectors } from 'ducks/orgs'
import { styled, Typography } from 'opensolar-ui'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { currencySymbolForCountry } from 'util/misc'
import { RecommendationItem, RecommendationsList } from '../shared/RecommendationsList'

interface BatteryRecommendationsListProps {
  batterySets: any[]
  selectedIndex: number | null
  onSelect: (index: number) => void
}

const TooltipContainer = styled('div')({
  padding: '8px 0',
  minWidth: '220px',
})

const TooltipTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '10px',
  lineHeight: '20px',
})

const TooltipRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  fontSize: '13px',
  lineHeight: '18px',
})

const TooltipLabel = styled('span')({
  color: '#6B7280',
  fontWeight: 400,
})

const TooltipValue = styled('span')({
  color: '#111827',
  fontWeight: 500,
  marginLeft: '12px',
  textAlign: 'right',
})

const buildTooltipContent = (
  batterySet: any,
  batteryNames: string,
  totalCapacity: number,
  quantity: number,
  index: number,
  currencySymbol: string
) => {
  const totalPeakPower = batterySet.total_peak_power_kw
  const totalContinuousPower = batterySet.total_power_max_continuous
  const warranty = batterySet.warranty_for_set
  const price = batterySet.price
  const gridIndependence = batterySet.grid_independence
  const selfConsumption = batterySet.self_consumption
  const firstYearSavings = batterySet.estimated_extra_first_year_savings

  // Format currency if price is available
  const formatCurrency = (value: number) => {
    return `${currencySymbol}${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  return (
    <TooltipContainer>
      <TooltipTitle>{batteryNames || `Option ${index + 1}`}</TooltipTitle>
      {totalCapacity > 0 && (
        <TooltipRow>
          <TooltipLabel>Total Capacity</TooltipLabel>
          <TooltipValue>{totalCapacity.toFixed(1)} kWh</TooltipValue>
        </TooltipRow>
      )}
      {totalPeakPower > 0 && (
        <TooltipRow>
          <TooltipLabel>Peak Power</TooltipLabel>
          <TooltipValue>{totalPeakPower.toFixed(1)} kW</TooltipValue>
        </TooltipRow>
      )}
      {totalContinuousPower > 0 && (
        <TooltipRow>
          <TooltipLabel>Continuous Power</TooltipLabel>
          <TooltipValue>{(totalContinuousPower / 1000).toFixed(1)} kW</TooltipValue>
        </TooltipRow>
      )}
      {quantity > 0 && (
        <TooltipRow>
          <TooltipLabel>Quantity</TooltipLabel>
          <TooltipValue>{quantity}</TooltipValue>
        </TooltipRow>
      )}
      {warranty && (
        <TooltipRow>
          <TooltipLabel>Warranty</TooltipLabel>
          <TooltipValue>{warranty} years</TooltipValue>
        </TooltipRow>
      )}
      {price !== undefined && price !== null && (
        <TooltipRow>
          <TooltipLabel>Price</TooltipLabel>
          <TooltipValue>{formatCurrency(price)}</TooltipValue>
        </TooltipRow>
      )}
      {gridIndependence !== undefined && gridIndependence !== null && (
        <TooltipRow>
          <TooltipLabel>Grid Independence</TooltipLabel>
          <TooltipValue>{gridIndependence.toFixed(1)}%</TooltipValue>
        </TooltipRow>
      )}
      {selfConsumption !== undefined && selfConsumption !== null && (
        <TooltipRow>
          <TooltipLabel>Self Consumption</TooltipLabel>
          <TooltipValue>{selfConsumption.toFixed(1)}%</TooltipValue>
        </TooltipRow>
      )}
      {firstYearSavings !== undefined && firstYearSavings !== null && firstYearSavings > 0 && (
        <TooltipRow>
          <TooltipLabel>First Year Savings</TooltipLabel>
          <TooltipValue>{formatCurrency(firstYearSavings)}</TooltipValue>
        </TooltipRow>
      )}
    </TooltipContainer>
  )
}

export const BatteryRecommendationsList: React.FC<BatteryRecommendationsListProps> = ({
  batterySets,
  selectedIndex,
  onSelect,
}) => {
  const countryIso2 = useSelector(orgSelectors.getOrgIso2)
  const currencySymbol = useMemo(() => currencySymbolForCountry(countryIso2), [countryIso2])

  const items: RecommendationItem[] = batterySets.map((batterySet: any, index: number) => {
    // Get the first battery for display (or combine if multiple)
    const firstBattery = batterySet.batteries?.[0]
    const code = firstBattery?.name || firstBattery?.code || `Option ${index + 1}`
    const manufacturer = firstBattery?.manufacturer_name || firstBattery?.manufacturer || undefined

    // For tooltip, show all batteries
    const batteryNames = batterySet.batteries
      ?.map((b: any) => {
        const mfr = b.manufacturer_name || b.manufacturer
        const batCode = b.name || b.code
        return mfr && batCode ? `${mfr} ${batCode}` : batCode || mfr || 'Unknown'
      })
      .filter(Boolean)
      .join(', ')
    const totalCapacity = batterySet.batteries?.reduce(
      (sum: number, b: any) => sum + (b.capacity_kwh || 0) * (b.quantity || 1),
      0
    )
    const quantity = batterySet.batteries?.reduce((sum: number, b: any) => sum + (b.quantity || 1), 0) || 0

    const tooltipContent = buildTooltipContent(batterySet, batteryNames, totalCapacity, quantity, index, currencySymbol)

    return {
      title: code,
      manufacturer,
      quantity,
      headlineFigure: totalCapacity > 0 ? { value: totalCapacity, unit: 'kWh' } : undefined,
      tooltipContent,
    }
  })

  return <RecommendationsList items={items} selectedIndex={selectedIndex} onSelect={onSelect} />
}
